import React, { useState } from "react";
import API_BASE from "../config/api";
import { resolveImageUrl } from "../utils/siteHelpers";

/*
  Direct file-upload field for a business's Gallery video (note item 5:
  "add option in Gallery section for upload video from browser & save it
  like [the existing] youtube link"). Same shape/behaviour as
  ImageUploadField.jsx, adapted for video: uploads immediately to
  /api/upload_business_file.php with purpose="video" and hands the
  returned absolute URL back through onChange.

  This is separate from the existing "YouTube video link" field (Home tab,
  business_site_data.youtube_link) — a business can offer either or both;
  the public site's Videos tab shows every video it has.

  props:
    value        - current stored video URL (string)
    onChange     - (newValue: string) => void
    personaluid  - used only to namespace the uploaded file's folder
*/
export default function VideoUploadField({ value, onChange, personaluid }) {
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState("");

    const previewSrc = resolveImageUrl(API_BASE, value);

    const handleFile = async (e) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = ""; // allow re-selecting the same file later
        if (!file) return;

        setError("");
        setUploading(true);

        const fd = new FormData();
        fd.append("file", file);
        fd.append("purpose", "video");
        if (personaluid) fd.append("personaluid", personaluid);

        try {
            const res = await fetch(`${API_BASE}/api/upload_business_file.php`, {
                method: "POST",
                body: fd,
            });
            const data = await res.json();
            setUploading(false);
            if (data.success) {
                onChange(data.url);
            } else {
                setError(data.message || "Upload failed.");
            }
        } catch {
            setUploading(false);
            setError("Network error while uploading.");
        }
    };

    return (
        <div className="dn-vidupload">
            <div className="dn-vidupload-row">
                <div className="dn-vidupload-preview">
                    {value ? (
                        <video src={previewSrc} muted preload="metadata" />
                    ) : (
                        <i className="bi bi-camera-reels" />
                    )}
                </div>

                <div className="flex-grow-1">
                    <label className="btn btn-sm btn-outline-primary mb-0">
                        <i className="bi bi-cloud-upload-fill me-1" />
                        {uploading ? "Uploading..." : value ? "Replace video" : "Upload a video"}
                        <input type="file" accept="video/*" hidden disabled={uploading} onChange={handleFile} />
                    </label>
                    {value && (
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-danger ms-2"
                            onClick={() => onChange("")}
                            disabled={uploading}
                        >
                            Remove
                        </button>
                    )}
                    <div className="text-muted" style={{ fontSize: 11.5 }}>MP4, WebM or MOV — up to 60MB.</div>
                </div>
            </div>

            {error && <div className="dn-vidupload-error"><i className="bi bi-exclamation-circle me-1" />{error}</div>}

            <style>{`
                .dn-vidupload-row{display:flex;gap:10px;align-items:center;}
                .dn-vidupload-preview{width:80px;height:56px;border-radius:8px;border:1px solid #dee2e6;flex-shrink:0;display:flex;align-items:center;justify-content:center;overflow:hidden;background:#f8f9fa;color:#adb5bd;font-size:20px;}
                .dn-vidupload-preview video{width:100%;height:100%;object-fit:cover;}
                .dn-vidupload-error{font-size:11.5px;color:#c0392b;margin-top:4px;}
            `}</style>
        </div>
    );
}